<template>
  <el-dialog
    class="required-email-notice"
    :visible.sync="visible"
    :title="$t('SendEmail.notice.title')"
    width="800px"
    top="5vh"
    :z-index="4000"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="preventClose"
    @closed="handleClosed"
  >
    <template slot="title">
      <div class="notice-title">
        <i class="el-icon-message-solid notice-icon" aria-hidden="true" />
        <span>{{ $t('SendEmail.notice.title') }}</span>
      </div>
    </template>
    <div class="notice-body">
      <template v-if="currentEmail">
        <div class="notice-field">
          <div class="notice-label">{{ $t('SendEmail.table.EmailTitle') }}</div>
          <div class="notice-value">{{ currentEmail.mailTitle }}</div>
        </div>
        <div class="notice-field">
          <div class="notice-label">{{ $t('SendEmail.table.EmailContent') }}</div>
          <div ref="content" class="notice-value notice-content">{{ currentEmail.content }}</div>
        </div>
        <div v-if="attachments.length" class="notice-field">
          <div class="notice-label">{{ $t('SendEmail.table.Multiples') }}</div>
          <div v-for="(file, index) in attachments" :key="index" class="notice-attachment">
            <i class="el-icon-document" aria-hidden="true" />
            <span class="attachment-name">{{ file.name }}</span>
            <el-button type="text" icon="el-icon-download" @click="downloadAttachment(file)" />
          </div>
        </div>
      </template>
    </div>
    <div slot="footer" class="notice-footer">
      <el-checkbox v-model="acknowledged" class="notice-acknowledgement" :disabled="loading || submitting || !currentEmail">
        {{ $t('SendEmail.notice.acknowledgement') }}
      </el-checkbox>
      <el-button type="primary" :disabled="!acknowledged || loading || !currentEmail" :loading="submitting" @click="confirmEmail">
        {{ $t('okText') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { funConfigApi } from '@/api';
import { openDownloadUrl } from '@/utils';

export default {
  name: 'RequiredEmailNotice',
  data() {
    return {
      visible: false,
      loading: false,
      submitting: false,
      acknowledged: false,
      emails: [],
      currentIndex: 0,
      finished: false,
    };
  },
  computed: {
    currentEmail() {
      return this.emails[this.currentIndex] || null;
    },
    attachments() {
      let annex = this.currentEmail && this.currentEmail.annex;
      if (typeof annex === 'string') {
        try {
          annex = JSON.parse(annex);
        } catch (error) {
          return [];
        }
      }
      return Array.isArray(annex) ? annex.filter(file => file && typeof file.url === 'string' && file.url.trim()) : [];
    },
  },
  watch: {
    visible(value) {
      if (value) document.body.classList.add('required-email-notice-open');
    },
    '$store.getters.token'() {
      // 登录失效或切换账号时清理旧账号的确认弹窗。
      this.$emit('complete', false);
    },
    '$store.getters.userId'() {
      this.$emit('complete', false);
    },
  },
  beforeDestroy() {
    document.body.classList.remove('required-email-notice-open');
  },
  methods: {
    preventClose() {
      // 仅在所有邮件确认成功后，由内部流程关闭弹窗。
    },
    async loadEmails() {
      if (this.loading) return;
      this.loading = true;
      try {
        const emails = await funConfigApi.getNeedConfirmEmailList();
        if (this._isDestroyed) return;
        if (!Array.isArray(emails) || emails.some(email => !email || !email.id)) {
          this.$emit('complete', false);
          return;
        }
        this.emails = emails;
        this.currentIndex = 0;
        this.acknowledged = false;
        this.visible = emails.length > 0;
        if (!emails.length) this.$emit('complete', true);
      } catch (error) {
        if (!this._isDestroyed) this.$emit('complete', false);
      } finally {
        this.loading = false;
      }
    },
    async confirmEmail() {
      if (!this.acknowledged || !this.currentEmail || this.submitting || this.loading) return;
      this.submitting = true;
      try {
        // generate 会移除路径参数，单独传入对象，避免修改原始邮件数据。
        const result = await funConfigApi.userConfirmEmail({ emailId: this.currentEmail.id });
        if (this._isDestroyed) return;
        if (!result) return;
        // 已经确认过的邮件按成功处理，继续下一封。
        if (result.failed && result.message !== 'email already confirmed') return;
        this.acknowledged = false;
        this.currentIndex += 1;
        if (this.currentEmail) {
          this.$nextTick(() => {
            if (this.$refs.content) this.$refs.content.scrollTop = 0;
          });
        } else {
          this.finish();
        }
      } catch (error) {
        // 错误提示由统一请求层处理，保留当前邮件。
      } finally {
        this.submitting = false;
      }
    },
    downloadAttachment(file) {
      openDownloadUrl(file.url, file.name);
    },
    finish() {
      this.finished = true;
      this.visible = false;
    },
    handleClosed() {
      document.body.classList.remove('required-email-notice-open');
      if (this.finished) this.$emit('complete', true);
    },
  },
};
</script>

<style>
/* 只在邮件确认期间限制常驻业务提示的层级，其他页面和管理员端沿用原样。 */
.required-email-notice-open .layout-order-notification {
  z-index: 3999 !important;
}
</style>

<style lang="scss" scoped>
.required-email-notice {
  ::v-deep .el-dialog {
    max-width: calc(100% - 32px);
    border-radius: 10px;
  }
  ::v-deep .el-dialog__header {
    padding: 24px 24px 0;
  }
  ::v-deep .el-dialog__footer {
    padding: 0 24px 24px;
  }
}
.notice-title {
  display: flex;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  font-size: 22px;
  font-weight: 600;
}
.notice-icon {
  margin-right: 16px;
  padding: 14px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 26px;
}
.notice-body {
  min-height: 120px;
  max-height: 60vh;
  overflow-y: auto;
}
.notice-field + .notice-field {
  margin-top: 16px;
}
.notice-label {
  margin-bottom: 8px;
  color: #606266;
  font-weight: 600;
}
.notice-value {
  padding: 10px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #f5f7fa;
  color: #303133;
  overflow-wrap: break-word;
}
.notice-content {
  max-height: 32vh;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.6;
}
.notice-attachment {
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding: 8px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
}
.attachment-name {
  flex: 1;
  min-width: 0;
  margin: 0 12px;
  overflow-wrap: break-word;
}
.notice-footer {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}
.notice-acknowledgement {
  display: flex;
  align-items: flex-start;
  margin: 0 0 16px;
  text-align: left;
  ::v-deep .el-checkbox__input {
    margin-top: 3px;
  }
  ::v-deep .el-checkbox__label {
    white-space: normal;
    line-height: 1.5;
  }
}
.notice-footer .el-button--primary {
  min-width: 120px;
}
</style>
